import type { Gender } from '@common/enums';
import type {
    AccountRequestRejectApiResponse,
    CheckMatchingEntityApiResponse,
    FetchMatchingEntityOptions,
    GetAccountRequestApproverForOrganizationApiResponse,
    GetAccountRequestsForOrganizationApiResponse,
    GetMatchingPractitionersApiResponse,
    GetOneAccountRequestApiResponse,
    GetSupervisorsApiResponse,
} from '@common/interfaces/api/accountRequestsApi';
import type {
    AccountRequestAddValidated,
    AccountRequestApproveValidated,
    AccountRequestUpdateApproverValidated,
} from '@common/validation/accountRequest';
import { ValidationErrorCode } from '@common/enums/validationErrorCode';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { fetchJson } from '@frontend/store/fetch';
import { removeUndefinedAndNullPropertiesFromObject } from '@frontend/utils/removeUndefinedAndNullPropertiesFromObject';

export async function getOneAccountRequest(
    accountRequestId: string,
    signal: AbortSignal
): Promise<GetOneAccountRequestApiResponse> {
    const url = `/api/accountRequests/${accountRequestId}`;
    const result = await fetchJson({ method: 'GET', signal, url });

    return result.json.accountRequest;
}

export async function approveAccountRequest(
    accountRequestId: string,
    matching: AccountRequestApproveValidated['match'],
    signal: AbortSignal
): Promise<void> {
    const url = `/api/accountRequests/${accountRequestId}/approve`;
    const body: AccountRequestApproveValidated = { match: matching };

    await fetchJson({ body, method: 'PUT', signal, url });
}

export async function getAccountRequestsForOrganization(
    organizationId: string,
    signal: AbortSignal
): Promise<GetAccountRequestsForOrganizationApiResponse> {
    const url = `/api/accountRequests/organization/${organizationId}`;
    const result = await fetchJson<GetAccountRequestsForOrganizationApiResponse>({ method: 'GET', signal, url });

    return result.json ?? { items: [] };
}

export async function rejectAccountRequest(
    accountRequestId: string,
    rejectionReason: string,
    signal: AbortSignal
): Promise<AccountRequestRejectApiResponse> {
    const url = `/api/accountRequests/${accountRequestId}/reject`;

    const result = await fetchJson<AccountRequestRejectApiResponse>({
        body: {
            rejectionReason,
        },
        method: 'PUT',
        signal,
        url,
    });

    return nonNullable(result.json);
}

export async function createAccountRequest(
    accountRequest: AccountRequestAddValidated,
    organizationId: string,
    signal: AbortSignal
): Promise<ValidationErrorCode | undefined> {
    const url = `/api/accountRequests`;

    const body: { accountRequest: AccountRequestAddValidated; organizationId: string } = {
        accountRequest,
        organizationId,
    };

    const result = await fetchJson({
        allowedErrorCodes: [
            ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS,
            ValidationErrorCode.IDENTIFIER_REGISTRATION_BIG_EXISTS,
        ],
        body,
        method: 'POST',
        signal,
        url,
    });

    return result.errorCode as ValidationErrorCode | undefined;
}

export async function getSupervisors(organizationId: string, onlyActives: boolean, signal: AbortSignal) {
    const url = `/api/accountRequests/organization/${organizationId}/supervisors?onlyActives=${onlyActives}`;
    const result = await fetchJson<GetSupervisorsApiResponse>({ method: 'GET', signal, url });

    return result.json ?? { items: [] };
}

export async function getAccountRequestApproversForOrganization(
    organizationId: string,
    accountRequestId: string | undefined,
    signal: AbortSignal
): Promise<GetAccountRequestApproverForOrganizationApiResponse> {
    const baseUrl = `/api/accountRequests/organization/${organizationId}/approvers`;
    const urlBuilder = new UrlBuilder(baseUrl);

    if (accountRequestId) {
        urlBuilder.add('accountRequestId', accountRequestId);
    }

    const url = urlBuilder.build();
    const result = await fetchJson<GetAccountRequestApproverForOrganizationApiResponse>({ method: 'GET', signal, url });

    return result.json ?? { items: [] };
}

export async function updateAccountRequestApprover({
    accountRequestId,
    needsKsyosApproval,
    approverId,
    signal,
}: {
    accountRequestId: string;
    approverId: string | null;
    needsKsyosApproval: boolean;
    signal: AbortSignal;
}): Promise<void> {
    const url = `/api/accountRequests/${accountRequestId}/approver`;

    const body: AccountRequestUpdateApproverValidated = {
        approverPractitionerId: approverId,
        needsKsyosApproval,
    };

    await fetchJson({ body, method: 'PUT', signal, url });
}

export async function fetchMatchingEntity(options: FetchMatchingEntityOptions, signal: AbortSignal) {
    const urlBuilder = new UrlBuilder('/api/accountRequests/checkMatchingEntity');
    const optionsWithoutEmptyValues = removeUndefinedAndNullPropertiesFromObject(options);

    urlBuilder.add(optionsWithoutEmptyValues);

    const url = urlBuilder.build();
    const result = await fetchJson<CheckMatchingEntityApiResponse>({ method: 'GET', signal, url });

    return nonNullable(result.json);
}

export async function getMatchingPractitioners(nameFamily: string, gender: Gender, signal: AbortSignal) {
    const urlBuilder = new UrlBuilder('/api/accountRequests/matchingPractitioners');

    urlBuilder.add({ gender, nameFamily });

    const url = urlBuilder.build();
    const result = await fetchJson<GetMatchingPractitionersApiResponse>({ method: 'GET', signal, url });

    return result.json?.practitioners ?? [];
}
